import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import {
  TabPaginationContainer,
  type TabItem,
} from "@/components/TabPaginationContainer";
import { RequestDetailSidebar, type SidebarTab } from "./RequestDetailSidebar";

interface Props {
  /** Content rendered inside the DETAILS tab of TabPaginationContainer. */
  detailContent: ReactNode;
  /** Tabs for the right ResizableSidebar. */
  sidebarTabs: SidebarTab[];
  /** Any dialogs/modals that must be rendered outside the layout grid. */
  dialogs?: ReactNode;
  sidebarDefaultWidth?: number;
  sidebarMinWidth?: number;
  sidebarMaxWidth?: number;
}

/**
 * Standard two-column shell for request detail pages.
 *
 * Left: `TabPaginationContainer` with a single selfContained DETAILS tab —
 * pass your header + info grid + footer as `detailContent`.
 *
 * Right: `RequestDetailSidebar` (ResizableSidebar + HorizontalTabs) — pass
 * tab definitions as `sidebarTabs` (value, label, content).
 *
 * Pass portaled dialogs/modals via `dialogs` — they are rendered outside the
 * flex grid so they aren't clipped.
 *
 * @see REQUEST_DETAIL_ARCHITECTURE.md for full usage guide.
 */
export const RequestDetailLayout = ({
  detailContent,
  sidebarTabs,
  dialogs,
  sidebarDefaultWidth,
  sidebarMinWidth,
  sidebarMaxWidth,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const leftTabs: TabItem[] = [
    {
      value: "details",
      label: t("common:timeline.details"),
      selfContained: true,
      contentClassName: "flex flex-col flex-1 min-h-0",
      content: detailContent,
    },
  ];

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-3 h-full">
        <div className="flex-1 min-w-0 flex flex-col">
          <TabPaginationContainer tabs={leftTabs} defaultValue="details" />
        </div>
        <RequestDetailSidebar
          tabs={sidebarTabs}
          defaultWidth={sidebarDefaultWidth}
          minWidth={sidebarMinWidth}
          maxWidth={sidebarMaxWidth}
        />
      </div>
      {dialogs}
    </>
  );
};
